/**
 * EmployeeHRPortal Component
 * Employee's personal HR portal for viewing onboarding tasks, documents, reviews, and holidays
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { openAuthenticatedFile } from '@/lib/api';
import { 
  ClipboardCheck, Star, FileText, Calendar, Upload,
  ChevronDown, ChevronUp, Check, X, Clock, AlertCircle, 
  Download, Eye, Loader2, User, CheckCircle, File, Plus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'id_card', label: 'ID Card / CNIC' },
  { value: 'passport', label: 'Passport' },
  { value: 'degree', label: 'Degree Certificate' },
  { value: 'resume', label: 'Resume / CV' },
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'contract', label: 'Employment Contract' },
  { value: 'bank_details', label: 'Bank Account Details' },
  { value: 'certification', label: 'Professional Certification' },
  { value: 'medical', label: 'Medical Certificate' },
  { value: 'other', label: 'Other Document' }
];

export const EmployeeHRPortal = () => {
  const [activeTab, setActiveTab] = useState('onboarding');
  const [loading, setLoading] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [myReviews, setMyReviews] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [newDocument, setNewDocument] = useState({
    document_type: '',
    document_name: '',
    expiry_date: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedReview, setExpandedReview] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchMyOnboarding();
    fetchMyReviews();
    fetchMyDocuments();
    fetchHolidays();
  }, []);

  const fetchMyOnboarding = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/employee/my-onboarding`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOnboardingProgress(data);
      }
    } catch (error) {
      console.error('Error fetching onboarding:', error);
    }
    setLoading(false);
  };

  const toggleMyTask = async (taskId, currentStatus) => {
    try {
      const res = await fetch(
        `${API_URL}/api/hr/employee/my-onboarding/task/${taskId}?completed=${!currentStatus}`,
        { method: 'PUT', headers }
      );
      if (res.ok) {
        fetchMyOnboarding();
        toast.success(`Task ${!currentStatus ? 'completed' : 'marked incomplete'}`);
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const fetchMyReviews = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employee/my-reviews`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMyReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const acknowledgeReview = async (reviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employee/reviews/${reviewId}/acknowledge`, {
        method: 'PUT',
        headers,
        
      });
      if (res.ok) {
        toast.success('Review acknowledged');
        fetchMyReviews();
      }
    } catch (error) {
      toast.error('Failed to acknowledge review');
    }
  };

  const fetchMyDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employee/my-documents`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMyDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const uploadDocument = async () => {
    if (!newDocument.document_type || !newDocument.document_name) {
      toast.error('Please fill document type and name');
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('document_type', newDocument.document_type);
      formData.append('document_name', newDocument.document_name);
      if (newDocument.expiry_date) {
        formData.append('expiry_date', newDocument.expiry_date);
      }
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const res = await fetch(`${API_URL}/api/hr/employee/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Document uploaded successfully');
        setShowUploadDialog(false);
        setNewDocument({ document_type: '', document_name: '', expiry_date: '' });
        setSelectedFile(null);
        fetchMyDocuments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Error uploading document');
    }
    setUploadingDoc(false);
  };

  const fetchHolidays = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/holidays`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHolidays(data);
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      acknowledged: 'bg-green-100 text-green-700',
      draft: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || styles.draft;
  };

  const tabs = [
    { id: 'onboarding', label: 'My Onboarding', icon: ClipboardCheck },
    { id: 'documents', label: 'My Documents', icon: FileText },
    { id: 'reviews', label: 'Performance Reviews', icon: Star },
    { id: 'holidays', label: 'Holiday Calendar', icon: Calendar }
  ];

  return (
    <div className="space-y-6" data-testid="employee-hr-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <User className="w-6 h-6 text-purple-600" />
          Employee HR Portal
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your onboarding tasks, documents, and view your performance reviews
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-slate-700 pb-4">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            className={activeTab === tab.id ? 'bg-purple-600 text-white' : 'text-gray-600'}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {/* Onboarding Tab */}
        {activeTab === 'onboarding' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-purple-600" />
                My Onboarding Checklist
              </h3>
              {onboardingProgress && (
                <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                  {onboardingProgress.completed_tasks || 0} / {onboardingProgress.total_tasks || 0} Complete
                </Badge>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : onboardingProgress?.tasks?.length > 0 ? (
              <div className="space-y-3">
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span>Progress</span>
                    <span>{Math.round((onboardingProgress.completed_tasks / onboardingProgress.total_tasks) * 100 || 0)}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-600 rounded-full transition-all duration-500"
                      style={{ width: `${(onboardingProgress.completed_tasks / onboardingProgress.total_tasks) * 100 || 0}%` }}
                    />
                  </div>
                </div>

                {/* Task List */}
                {onboardingProgress.tasks.map(task => (
                  <div
                    key={task.task_id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      task.completed 
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                        : 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleMyTask(task.task_id, task.completed)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          task.completed 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-gray-300 dark:border-gray-500 hover:border-purple-400'
                        }`}
                      >
                        {task.completed && <Check className="w-4 h-4" />}
                      </button>
                      <div>
                        <p className={`font-medium ${task.completed ? 'text-green-700 dark:text-green-400 line-through' : 'text-gray-800 dark:text-white'}`}>
                          {task.task_name}
                        </p>
                        {task.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">{task.description}</p>
                        )}
                      </div>
                    </div>
                    {task.due_days && !task.completed && (
                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                        Due in {task.due_days} days
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-gray-600 dark:text-gray-400">No onboarding tasks assigned yet</p>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                My Documents
              </h3>
              <Button 
                onClick={() => setShowUploadDialog(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </div>

            {myDocuments.length > 0 ? (
              <div className="grid gap-4">
                {myDocuments.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                        <File className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">{doc.document_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {doc.expiry_date && (
                        <Badge className={`text-xs ${
                          new Date(doc.expiry_date) < new Date() 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {new Date(doc.expiry_date) < new Date() ? 'Expired' : `Expires: ${doc.expiry_date}`}
                        </Badge>
                      )}
                      <Badge className={`text-xs ${
                        doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {doc.status || 'Pending'}
                      </Badge>
                      {doc.file_url && (
                        <Button variant="ghost" size="sm" onClick={() => openAuthenticatedFile(doc.file_url).catch(() => toast.error('Failed to open document'))}>
                            <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-4">No documents uploaded yet</p>
                <Button 
                  onClick={() => setShowUploadDialog(true)}
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Upload Your First Document
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-purple-600" />
              My Performance Reviews
            </h3>

            {myReviews.length > 0 ? (
              <div className="space-y-4">
                {myReviews.map(review => (
                  <div
                    key={review.id}
                    className="border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden"
                  >
                    <div 
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700 cursor-pointer"
                      onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                    >
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">
                          Review Period: {review.review_period_start} to {review.review_period_end}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          By: {review.reviewer_name || 'Manager'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map(star => (
                            <Star 
                              key={star}
                              className={`w-4 h-4 ${star <= review.overall_rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <Badge className={getStatusBadge(review.status)}>
                          {review.status}
                        </Badge>
                        {expandedReview === review.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                    
                    {expandedReview === review.id && (
                      <div className="p-4 space-y-4 border-t border-gray-200 dark:border-slate-600">
                        {review.strengths && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Strengths</p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{review.strengths}</p>
                          </div>
                        )}
                        {review.areas_for_improvement && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Areas for Improvement</p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{review.areas_for_improvement}</p>
                          </div>
                        )}
                        {review.goals_for_next_period && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Goals for Next Period</p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{review.goals_for_next_period}</p>
                          </div>
                        )}
                        {review.comments && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Additional Comments</p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{review.comments}</p>
                          </div>
                        )}
                        
                        {review.status === 'submitted' && (
                          <div className="pt-4 border-t border-gray-200 dark:border-slate-600">
                            <Button
                              onClick={() => acknowledgeReview(review.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Acknowledge Review
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-600 dark:text-gray-400">No performance reviews yet</p>
              </div>
            )}
          </div>
        )}

        {/* Holidays Tab */}
        {activeTab === 'holidays' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-purple-600" />
              Company Holiday Calendar
            </h3>

            {holidays.length > 0 ? (
              <div className="space-y-3">
                {holidays
                  .filter(h => new Date(h.date) >= new Date(new Date().setHours(0,0,0,0)))
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map(holiday => (
                    <div
                      key={holiday.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-16">
                          <p className="text-2xl font-bold text-purple-600">
                            {new Date(holiday.date).getDate()}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 dark:text-white">{holiday.name}</p>
                          {holiday.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{holiday.description}</p>
                          )}
                        </div>
                      </div>
                      {holiday.is_office_closed && (
                        <Badge className="bg-red-100 text-red-700">Office Closed</Badge>
                      )}
                    </div>
                  ))}
                {holidays.filter(h => new Date(h.date) >= new Date(new Date().setHours(0,0,0,0))).length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No upcoming holidays scheduled</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-600 dark:text-gray-400">Holiday calendar not available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Document Type *</label>
              <Select
                value={newDocument.document_type}
                onValueChange={(v) => setNewDocument({ ...newDocument, document_type: v })}
              >
                <SelectTrigger className="bg-white dark:bg-slate-700">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-700">
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Document Name *</label>
              <Input
                value={newDocument.document_name}
                onChange={(e) => setNewDocument({ ...newDocument, document_name: e.target.value })}
                placeholder="e.g., My CNIC Front"
                className="bg-white dark:bg-slate-700"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Expiry Date (if applicable)</label>
              <Input
                type="date"
                value={newDocument.expiry_date}
                onChange={(e) => setNewDocument({ ...newDocument, expiry_date: e.target.value })}
                className="bg-white dark:bg-slate-700"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Upload File</label>
              <Input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="bg-white dark:bg-slate-700"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
              <p className="text-xs text-gray-500 mt-1">Accepted: PDF, JPG, PNG, DOC, DOCX</p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={uploadDocument}
                disabled={uploadingDoc}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {uploadingDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeHRPortal;
